import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import {
  getPaymentAccounts, createPaymentAccount, updatePaymentAccount, deletePaymentAccount,
  type PaymentAccount,
} from '../api';

// Konti der bruges til at afstemme betalinger.
// GoMore er pre-defineret og kan ikke slettes — kun deaktiveres.
// Brugeren kan oprette egne (Bank, MobilePay, Kontant osv.).
export default function PaymentAccountsSection() {
  const [accounts, setAccounts] = useState<PaymentAccount[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [editing, setEditing]   = useState<PaymentAccount | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try { setAccounts(await getPaymentAccounts()); }
    catch (e) { setError(e instanceof Error ? e.message : 'Kunne ikke hente konti'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id: number) => {
    setError(null);
    try { await deletePaymentAccount(id); await load(); setConfirmDelete(null); }
    catch (e) { setError(e instanceof Error ? e.message : 'Kunne ikke slette'); }
  };

  return (
    <>
      <h3 className="section-title mt-8 mb-3">Betalings-konti</h3>
      <div className="bg-white rounded-xl shadow-sm border divide-y mb-6">
        <div className="px-6 py-3 flex items-center justify-between">
          <p className="text-xs text-gray-500">
            Bruges til at afstemme betalinger. GoMore er indbygget; tilføj fx Bank, MobilePay eller Kontant.
          </p>
          <button onClick={() => setCreating(true)} className="btn btn-secondary text-xs inline-flex items-center gap-1.5">
            <Plus size={13} /> Tilføj konto
          </button>
        </div>

        {error && <div className="px-6 py-2 text-sm text-rose-700 bg-rose-50">{error}</div>}

        {loading ? (
          <div className="px-6 py-4 text-sm text-gray-500">Indlæser…</div>
        ) : (
          <ul>
            {accounts.map(a => (
              <li key={a.id} className="px-6 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{a.name}</span>
                    <span className="text-xs text-gray-400 font-mono">{a.source_key}</span>
                    {!a.is_active && <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">Inaktiv</span>}
                    {a.is_built_in === 1 && <span className="text-xs bg-sage-100 text-sage-700 px-1.5 py-0.5 rounded">Indbygget</span>}
                  </div>
                  {a.notes && <p className="text-xs text-gray-500 mt-0.5">{a.notes}</p>}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => setEditing(a)} title="Redigér"
                    className="w-8 h-8 flex items-center justify-center rounded text-gray-400 hover:bg-cream-100 hover:text-gray-700">
                    <Pencil size={13} />
                  </button>
                  {a.is_built_in !== 1 && (
                    confirmDelete === a.id ? (
                      <>
                        <button onClick={() => handleDelete(a.id)} className="text-xs text-rose-700 hover:underline px-2">Slet</button>
                        <button onClick={() => setConfirmDelete(null)} className="text-gray-300 hover:text-gray-700"><X size={13} /></button>
                      </>
                    ) : (
                      <button onClick={() => setConfirmDelete(a.id)} title="Slet"
                        className="w-8 h-8 flex items-center justify-center rounded text-gray-400 hover:bg-rose-50 hover:text-rose-700">
                        <Trash2 size={13} />
                      </button>
                    )
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {(editing || creating) && (
        <AccountModal
          initial={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={async () => { setEditing(null); setCreating(false); await load(); }}
        />
      )}
    </>
  );
}

interface AccountModalProps {
  initial: PaymentAccount | null;
  onClose: () => void;
  onSaved: () => void;
}

function AccountModal({ initial, onClose, onSaved }: AccountModalProps) {
  const [name, setName]           = useState(initial?.name ?? '');
  const [sourceKey, setSourceKey] = useState(initial?.source_key ?? '');
  const [isActive, setIsActive]   = useState(initial?.is_active !== 0);
  const [notes, setNotes]         = useState(initial?.notes ?? '');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState<string | null>(null);

  // Auto-generér source_key fra navn ved oprettelse (men bevar manuelt indtastede ændringer)
  const [keyTouched, setKeyTouched] = useState(false);
  const handleNameChange = (v: string) => {
    setName(v);
    if (!initial && !keyTouched) {
      setSourceKey(v.toLowerCase().replace(/[æø]/g, 'e').replace(/å/g, 'aa').replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''));
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = { name: name.trim(), source_key: sourceKey.trim(), is_active: isActive, notes: notes.trim() || null };
      if (initial) await updatePaymentAccount(initial.id, payload);
      else         await createPaymentAccount(payload);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fejl');
      setSaving(false);
    }
  };

  const isBuiltIn = initial?.is_built_in === 1;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 sm:p-6 border-b">
          <h2 className="modal-title">{initial ? 'Redigér konto' : 'Ny betalings-konto'}</h2>
          <button onClick={onClose} className="modal-close">&times;</button>
        </div>
        <div className="p-4 sm:p-6 space-y-3">
          <div>
            <label className="label">Navn</label>
            <input className="input w-full" value={name} onChange={e => handleNameChange(e.target.value)} placeholder="Bank, MobilePay, Kontant…" />
          </div>
          <div>
            <label className="label">
              Tekn. nøgle <span className="text-xs text-gray-400 font-normal">(intern identifikation, kan ikke ændres senere på indbyggede)</span>
            </label>
            <input className="input w-full font-mono text-xs" value={sourceKey} onChange={e => { setSourceKey(e.target.value); setKeyTouched(true); }}
              disabled={isBuiltIn} placeholder="bank" />
          </div>
          <div>
            <label className="label">Note (valgfri)</label>
            <textarea className="input w-full" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
          <label className="checkbox-label">
            <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="rounded border-gray-300" />
            Aktiv (vises i dropdown ved betalings-registrering)
          </label>

          {error && <p className="text-sm text-rose-700">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="btn btn-secondary">Annullér</button>
            <button onClick={handleSubmit} disabled={saving || !name.trim() || !sourceKey.trim()}
              className="btn btn-primary inline-flex items-center gap-1.5">
              <Check size={14} /> {saving ? 'Gemmer…' : 'Gem'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
